import type { TagScope } from '@gramtee/shared';

/**
 * Background+border+text class tints applied to a tag chip based on its
 * scope. Used both:
 *   - in read-only chip lists (PlanExercise / ExercisePractice / history), and
 *   - through `TagSelector`'s `chipClassName` per-option, so the editable
 *     selectors render with the same scope cues.
 *
 * The `COMMON` value intentionally matches the pre-scope gray chip style
 * to avoid a visual churn for the existing common-tag use cases.
 */
export const TAG_CHIP_CLASSES: Record<TagScope, string> = {
  COMMON:
    'bg-gray-50 border-gray-200 text-gray-700 ' +
    'dark:bg-gray-800/60 dark:border-gray-700 dark:text-gray-200',
  // Friendly green tint for the learner's own tags. Picked from the `green-`
  // palette (not `emerald-`) so it doesn't read as the "correct answer" badge
  // that already uses emerald in ExerciseHistoryCard.
  PERSONAL:
    'bg-green-50 border-green-200 text-green-800 ' +
    'dark:bg-green-900/40 dark:border-green-800 dark:text-green-100',
  // Notepad-yellow tint for admin-only tags — mirrors the highlighter look
  // the user associates with "internal notes" / draft-like metadata.
  ADMIN:
    'bg-yellow-50 border-yellow-200 text-yellow-800 ' +
    'dark:bg-yellow-900/40 dark:border-yellow-800 dark:text-yellow-100',
};

/** Base layout/border-radius/padding for a read-only tag chip; combine with
 *  the per-scope tint above. The editable `TagSelector` already owns its own
 *  base style and only consumes the tint via `chipClassName`. */
export const TAG_CHIP_BASE_CLS = 'inline-flex items-center rounded-md border px-2 py-0.5 text-xs';
